import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { BookingEmbed } from "./BookingEmbed";

export const FinalCtaSection = () => {
  const scrollToScheduler = () => {
    const el = document.getElementById("booking-calendar");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section
      id="contact"
      className="relative w-full py-24 sm:py-32 overflow-hidden"
      style={{
        background: `
          radial-gradient(circle at 68% 38%, rgba(103, 214, 255, 0.30) 0%, transparent 58%),
          radial-gradient(circle at 20% 70%, rgba(68, 242, 99, 0.10) 0%, transparent 45%),
          linear-gradient(135deg, #25B7FB 0%, #159FF8 28%, #0A8FF5 62%, #0879EA 100%)
        `,
      }}
    >
      {/* Huge Translucent LET'S BUILD Background Typography */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-[4%] z-0 select-none font-heading font-black tracking-[-0.07em] text-[200px] sm:text-[300px] lg:text-[420px] text-white/[0.10] leading-[0.78] whitespace-nowrap text-center"
      >
        LET’S BUILD
      </div>

      <div className="relative z-10 max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Top Eyebrow */}
        <div className="flex items-center justify-center gap-2 mb-6 sm:mb-8">
          <span className="w-2.5 h-2.5 rounded-full bg-[#44F263] shadow-[0_0_12px_#44F263]" />
          <span className="text-[11px] sm:text-[13px] font-heading font-bold uppercase tracking-[0.18em] text-white">
            READY TO BUILD?
          </span>
        </div>

        {/* Main Headline */}
        <div className="text-center max-w-5xl mx-auto mb-6 sm:mb-8">
          <h2 className="font-heading font-black tracking-[-0.05em] text-[46px] sm:text-[68px] lg:text-[96px] xl:text-[104px] leading-[0.88] sm:leading-[0.92]">
            <span className="text-white block">Your System Is Either</span>
            <span className="text-[#44F263] block drop-shadow-[0_2px_18px_rgba(68,242,99,0.35)]">
              Building Revenue
            </span>
            <span className="text-white block">or Losing It.</span>
            <span className="text-white/95 block mt-2 text-[38px] sm:text-[56px] lg:text-[76px] xl:text-[84px] font-bold">
              Let’s Fix That.
            </span>
          </h2>

          <p className="font-sans text-[16px] sm:text-[19px] text-white/90 leading-[1.55] max-w-3xl mx-auto mt-6 sm:mt-8">
            I work with a small number of companies at a time to ensure I can
            deliver the depth of architectural thinking that produces real
            results. If your revenue system needs an upgrade, or if you&apos;re
            building one from scratch, let&apos;s talk.
          </p>
        </div>

        {/* Availability Card + Handwritten Annotation Container */}
        <div className="relative max-w-[840px] mx-auto mb-8 sm:mb-10">
          {/* Handwritten Annotation: "Your move." */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-4 sm:-right-16 -top-8 sm:-top-10 hidden md:flex items-center gap-2 text-white font-serif italic text-[24px] sm:text-[30px] select-none rotate-[-6deg]"
          >
            <span>Your move.</span>
            <svg
              width="48"
              height="48"
              viewBox="0 0 48 48"
              fill="none"
              className="text-white/90 translate-y-3 -rotate-12"
            >
              <path
                d="M10 12 C 20 8, 36 14, 34 34 M 34 34 L 26 30 M 34 34 L 38 24"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {/* White Availability Panel */}
          <motion.div
            initial={{ opacity: 1, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-[24px] sm:rounded-[28px] bg-white/[0.98] border border-white/60 p-6 sm:p-8 shadow-[0_18px_52px_rgba(4,56,125,0.18)] text-[#071E61]"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#EAF5FF] border border-[#0B7CF2]/20 flex items-center justify-center shrink-0 text-[#0B7CF2] shadow-xs text-xl">
                  📅
                </div>
                <div>
                  <div className="text-[11px] font-heading font-bold text-[#0B7CF2] tracking-[0.14em] uppercase">
                    CURRENT AVAILABILITY
                  </div>
                  <h3 className="text-[20px] sm:text-[24px] font-heading font-extrabold text-[#071E61] leading-tight mt-0.5">
                    2 engagement slots remaining this quarter.
                  </h3>
                  <p className="text-[13px] sm:text-[14px] text-[#536B89] mt-1">
                    Engagements typically begin within 2 weeks of first call.
                  </p>
                </div>
              </div>

              {/* Progress + Slot text */}
              <div className="sm:text-right shrink-0">
                <div className="text-[12px] font-heading font-bold text-[#536B89]">
                  3 of 5 slots taken this quarter.
                </div>
                {/* 5-segment Capacity Bar */}
                <div className="flex items-center gap-1.5 mt-2 w-full sm:w-44">
                  <span className="h-2.5 flex-1 rounded-full bg-gradient-to-r from-[#1DDB59] to-[#44F263]" />
                  <span className="h-2.5 flex-1 rounded-full bg-gradient-to-r from-[#1DDB59] to-[#44F263]" />
                  <span className="h-2.5 flex-1 rounded-full bg-gradient-to-r from-[#1DDB59] to-[#44F263]" />
                  <span className="h-2.5 flex-1 rounded-full bg-[#E8EEF7]" />
                  <span className="h-2.5 flex-1 rounded-full bg-[#E8EEF7]" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Primary CTA + Micro Trust Row */}
        <div className="flex flex-col items-center justify-center text-center mb-12 sm:mb-16">
          <Button
            type="button"
            onClick={scrollToScheduler}
            className="h-[60px] sm:h-[64px] px-8 sm:px-10 rounded-full bg-white hover:bg-[#F3F9FF] text-[#071E61] font-heading font-extrabold text-[16px] sm:text-[17px] shadow-[0_12px_32px_rgba(4,56,125,0.20)] hover:shadow-[0_16px_40px_rgba(4,56,125,0.26)] hover:-translate-y-0.5 transition-all duration-200 group border-0"
          >
            <span>Choose a Date &amp; Time →</span>
          </Button>

          {/* Micro Trust Row */}
          <div className="flex items-center justify-center flex-wrap gap-4 sm:gap-6 mt-4 text-white/90 text-[12px] sm:text-[14px] font-sans">
            <span className="inline-flex items-center gap-1.5">
              <span>⏱</span>
              <span>30 min</span>
            </span>
            <span className="text-white/40">|</span>
            <span className="inline-flex items-center gap-1.5">
              <span>💳</span>
              <span>No payment</span>
            </span>
            <span className="text-white/40">|</span>
            <span className="inline-flex items-center gap-1.5">
              <span>🎯</span>
              <span>Discovery only</span>
            </span>
          </div>
        </div>

        {/* Large White Booking Panel — shared embed (same one used on /book) */}
        <div
          id="booking-calendar"
          className="rounded-[28px] sm:rounded-[36px] bg-white border border-[#084667]/[0.08] shadow-[0_24px_74px_rgba(4,56,125,0.18)] p-4 sm:p-6 lg:p-8 overflow-hidden"
        >
          <BookingEmbed />
        </div>
      </div>
    </section>
  );
};
